import * as React from 'react';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import {
  CODE_TO_NAME_UNKIBBLE,
  CODE_TO_NAME_FRESH,
  DEFAULT_NUTRIENT_ANALYSIS_RECIPE_ID,
} from './constants';
import { NutrientAnalysisInfo, Option } from './types';
import { getNutrientAnalysisInfo } from '../api/getNutrientAnalysisInfo';
import { Dropdown } from '../component/Dropdown';
import { TableProductInfo } from '../component/TableProductInfo';
import { NutrientAnalysisHeaders } from '../tableHeaders';
import { BOTTOM_TOAST, generateErrorMsg } from '../utils';

export const NutrientAnalysis = () => {
  const [nutrientAnalysis, setNutrientAnalysis] = useState<NutrientAnalysisInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const recipeIds: Option[] = [
    ...Object.entries(CODE_TO_NAME_UNKIBBLE).flatMap(([code, name]) => [
      { label: code + ' ' + name, value: code },
    ]),
    ...Object.entries(CODE_TO_NAME_FRESH).flatMap(([code, name]) => [
      { label: code + ' ' + name, value: code },
    ]),
  ];
  const [selectedRecipeId, setSelectedRecipeId] = useState<Option>({
    label:
      DEFAULT_NUTRIENT_ANALYSIS_RECIPE_ID +
      ' ' +
      CODE_TO_NAME_UNKIBBLE[DEFAULT_NUTRIENT_ANALYSIS_RECIPE_ID],
    value: DEFAULT_NUTRIENT_ANALYSIS_RECIPE_ID,
  });

  useEffect(() => {
    getNutrientAnalysisInfo({ recipe_id: selectedRecipeId.value })
      .then((res) => {
        setNutrientAnalysis(res.data);
      })
      .catch((err) => {
        toast.error(generateErrorMsg(err), BOTTOM_TOAST);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [selectedRecipeId]);

  const firstHeaderGroups = [
    { label: selectedRecipeId.label, colSpan: 4, className: 'bg-green-200' },
  ];

  const transformProductToTableRow = (product: NutrientAnalysisInfo) => {
    return {
      '': product.nutrient_category,
      ' ': product.name,
      'As Fed Basis\n(indicates presence of nutrient as it is received)': product.as_fed_basis,
      'Dry Matter Basis\n(indicates presence of nutrient when all moisture is removed)':
        product.dry_matter_basis,
    };
  };

  return (
    <div className="h-screen flex flex-col">
      <div className="p-10 flex-shrink-0">
        <h1>Nutrient Analysis</h1>
      </div>

      {!loading && (
        <div className="px-10 mb-6">
          <div className="bg-gray-50 p-4 rounded-lg border">
            <h3 className="text-lg font-semibold mb-4">Filter</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
              <div className="flex items-center gap-4">
                <label className="text-sm font-medium whitespace-nowrap">Recipe Id</label>
                <div className="min-w-0 flex-1">
                  <Dropdown
                    options={recipeIds}
                    selectedOption={selectedRecipeId}
                    setSelectedOption={setSelectedRecipeId}
                    defaultText="Select Recipe Id"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
          <span className="ml-2">Loading...</span>
        </div>
      ) : (
        <div className="px-10 flex-1 flex flex-col">
          <div className="flex-1 border border-gray-300 rounded-lg">
            <TableProductInfo
              header={NutrientAnalysisHeaders}
              data={nutrientAnalysis.map((product: NutrientAnalysisInfo) =>
                transformProductToTableRow(product)
              )}
              topHeaderGroups={firstHeaderGroups}
            />
          </div>
        </div>
      )}
    </div>
  );
};
